import React, { useState, useEffect, useRef } from 'react';
import { ConfirmRemoveBundleDialog } from '@/components/ConfirmRemoveBundleDialog';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Scan, Check, X, AlertTriangle, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import * as fobApi from '@/services/fobSectionApi';
import * as authApi from '@/services/authApi';
import { useOperationDraft } from '@/hooks/useOperationDraft';
import { DraftsMenu } from '@/components/DraftsMenu';
import { ResumedDraftBanner } from '@/components/ResumedDraftBanner';
import { DeliveryPoSummary } from '@/components/po/DeliveryPoSummary';

const BundleReceiptEntry = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [consignments, setConsignments] = useState([]);
  const [selectedConsignment, setSelectedConsignment] = useState(null);
  const [bundleEntry, setBundleEntry] = useState(null);
  const [bundleInput, setBundleInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [resumedDraft, setResumedDraft] = useState(null);
  const bundleInputRef = useRef(null);

  const {
    drafts, activeDraftId, saving, lastSavedAt,
    autoSave, clearDraft, loadDraft, removeDraft, setActiveDraftId,
  } = useOperationDraft({ moduleKey: 'section:FOB', operationType: 'bundle_receipt', allowZero: true });

  // Scans persist server-side per scan, so the draft is a resumable bookmark of
  // the in-progress consignment + received count.
  useEffect(() => {
    if (!selectedConsignment) return;
    const received = bundleEntry?.received_bundles?.length || 0;
    autoSave({
      label: `${selectedConsignment.document_barcode} — ${received} received`,
      bundleCount: received,
      documentId: selectedConsignment.entry_id,
      documentNo: selectedConsignment.document_barcode,
      payload: { entryId: selectedConsignment.entry_id, documentBarcode: selectedConsignment.document_barcode },
    });
  }, [bundleEntry, selectedConsignment, autoSave]);

  const handleResumeDraft = async (draftId) => {
    try {
      const doc = await loadDraft(draftId);
      const entryId = doc.payload?.entryId;
      if (entryId) await handleSelectConsignment(entryId, true);
      const summary = (drafts || []).find((x) => x.draft_id === draftId) || null;
      setResumedDraft(summary);
      toast.success(summary?.last_updated_by_name ? `Resumed draft — last worked on by ${summary.last_updated_by_name}` : 'Draft restored');
    } catch {
      toast.error('Failed to restore draft');
    }
  };

  useEffect(() => {
    fetchUser();
    fetchConsignments();
  }, []);

  useEffect(() => {
    // Focus on bundle input when consignment is selected
    if (selectedConsignment && bundleInputRef.current) {
      bundleInputRef.current.focus();
    }
  }, [selectedConsignment]);

  const fetchUser = async () => {
    try {
      const userData = await authApi.getMe();
      setUser(userData);
    } catch (error) {
      console.error('Error fetching user:', error);
      navigate('/login');
    }
  };

  const fetchConsignments = async () => {
    setIsLoading(true);
    try {
      const data = await fobApi.getShippedConsignments();
      setConsignments(data.consignments || []);
    } catch (error) {
      console.error('Error fetching consignments:', error);
      toast.error('Failed to load consignments');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectConsignment = async (entryId, fromDraft = false) => {
    if (!fromDraft) {
      const existing = (drafts || []).find((d) => d.document_id === entryId);
      if (existing) { await handleResumeDraft(existing.draft_id); return; }
      setResumedDraft(null);
    }
    const consignment = consignments.find(c => c.entry_id === entryId);
    setSelectedConsignment(consignment);
    setErrorMessage('');
    if (!fromDraft) setActiveDraftId(null); // manual pick starts a fresh bookmark
    
    try {
      // Start or get bundle entry
      const entry = await fobApi.startBundleReceipt(entryId);
      setBundleEntry(entry);
    } catch (error) {
      console.error('Error starting bundle receipt:', error);
      toast.error(error.response?.data?.detail || 'Failed to start bundle receipt');
    }
  };

  const handleScanBundle = async (e) => {
    e.preventDefault();
    
    if (!bundleInput.trim() || !selectedConsignment) return;
    
    setIsScanning(true);
    setErrorMessage('');
    
    try {
      const result = await fobApi.scanBundle(selectedConsignment.entry_id, bundleInput.trim());
      toast.success(result.message);
      
      // Refresh bundle entry
      const entry = await fobApi.getBundleEntry(selectedConsignment.entry_id);
      setBundleEntry(entry);
      setBundleInput('');

      // AUTO-CLOSE: the backend finalises the receipt on the last bundle.
      if (result.auto_completed || entry.status === 'completed') {
        toast.success('All bundles received — receipt completed');
        resetReceipt();
        return;
      }
      
      // Focus back on input after state updates
      setTimeout(() => {
        if (bundleInputRef.current) {
          bundleInputRef.current.focus();
        }
      }, 100);
    } catch (error) {
      console.error('Error scanning bundle:', error);
      const errorDetail = error.response?.data?.detail || 'Failed to scan bundle';
      setErrorMessage(errorDetail);
      toast.error(errorDetail);
      setBundleInput('');
      
      // Keep focus on input for retry
      setTimeout(() => {
        if (bundleInputRef.current) {
          bundleInputRef.current.focus();
        }
      }, 100);
    } finally {
      setIsScanning(false);
    }
  };

  const [removeBundleTarget, setRemoveBundleTarget] = useState(null);

  const handleRemoveBundle = async (bundleBarcode) => {
    try {
      await fobApi.removeBundle(selectedConsignment.entry_id, bundleBarcode);
      toast.success('Bundle removed');
      
      // Refresh bundle entry
      const entry = await fobApi.getBundleEntry(selectedConsignment.entry_id);
      setBundleEntry(entry);
    } catch (error) {
      console.error('Error removing bundle:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove bundle');
    }
  };

  const resetReceipt = () => {
    clearDraft();
    setSelectedConsignment(null);
    setBundleEntry(null);
    setResumedDraft(null);
    fetchConsignments();
  };

  const handleStartFresh = async () => {
    const id = resumedDraft?.draft_id;
    if (id) {
      const ok = await removeDraft(id);
      if (!ok) { toast.error('Could not discard the shared draft — please try again'); return; }
    }
    setResumedDraft(null);
    resetReceipt();
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/sections/fob')} className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Package className="w-6 h-6" />
              <div>
                <h1 className="text-xl font-bold font-display">Bundle Receipt Entry</h1>
                <p className="text-xs text-[#E8DCC8]">FOB Section - Operation 1</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <DraftsMenu
              drafts={drafts}
              onResume={handleResumeDraft}
              onDelete={removeDraft}
              activeDraftId={activeDraftId}
              saving={saving}
              lastSavedAt={lastSavedAt}
              variant="dark"
            />
            <Button onClick={fetchConsignments} variant="secondary" size="sm">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {resumedDraft && selectedConsignment && (
          <ResumedDraftBanner draft={resumedDraft} onStartFresh={handleStartFresh} className="mb-4" />
        )}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Document Selection & Scanning */}
          <div className="lg:col-span-1 space-y-4">
            {/* Document Selection */}
            <div className="bg-white rounded-lg border border-[#E5DFD0] p-4">
              <Label className="text-sm font-semibold text-[#1A1F2E]">Select Document</Label>
              <Select
                value={selectedConsignment?.entry_id || ''}
                onValueChange={handleSelectConsignment}
                disabled={isLoading}
              >
                <SelectTrigger className="mt-2" data-testid="document-select">
                  <SelectValue placeholder={isLoading ? "Loading..." : "Select document"} />
                </SelectTrigger>
                <SelectContent>
                  {consignments.map((consignment) => (
                    <SelectItem key={consignment.entry_id} value={consignment.entry_id}>
                      <div className="flex flex-col">
                        <span className="font-medium">
                          {consignment.document_barcode}
                          {(drafts || []).some((d) => d.document_id === consignment.entry_id) && (
                            <span className="ml-2 font-semibold text-amber-600">· Draft</span>
                          )}
                        </span>
                        <span className="text-xs text-gray-500">{consignment.vendor_name || 'N/A'}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {selectedConsignment && (
                <div className="mt-3 p-3 bg-[#F5F1E8] rounded-lg text-sm">
                  <div><span className="text-[#5A5F6E]">Vendor:</span> <strong>{selectedConsignment.vendor_name || 'N/A'}</strong></div>
                  <div><span className="text-[#5A5F6E]">Total Bundles:</span> <strong>{selectedConsignment.bundle_count}</strong></div>
                  <div><span className="text-[#5A5F6E]">Shipped:</span> <strong>{formatDateTime(selectedConsignment.shipped_at)}</strong></div>
                </div>
              )}
              {selectedConsignment && (
                <DeliveryPoSummary entryId={selectedConsignment.entry_id} refreshKey={bundleEntry?.received_bundles?.length || 0} className="mt-3" />
              )}
            </div>

            {/* Bundle Scanning */}
            {selectedConsignment && bundleEntry && bundleEntry.status !== 'completed' && (
              <div className="bg-white rounded-lg border border-[#E5DFD0] p-4">
                <Label className="text-sm font-semibold text-[#1A1F2E]">Scan Bundle Barcode</Label>
                <form onSubmit={handleScanBundle} className="mt-2">
                  <div className="flex gap-2">
                    <Input
                      ref={bundleInputRef}
                      value={bundleInput}
                      onChange={(e) => setBundleInput(e.target.value)}
                      placeholder="Scan or enter bundle barcode"
                      className="flex-1"
                      disabled={isScanning}
                      autoFocus
                      data-testid="bundle-input"
                    />
                    <Button 
                      type="submit" 
                      disabled={isScanning || !bundleInput.trim()}
                      className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                      data-testid="fob-scan-btn"
                    >
                      {isScanning ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Scan className="w-4 h-4" />}
                    </Button>
                  </div>
                </form>
                
                {/* Error Message */}
                {errorMessage && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <div>{errorMessage}</div>
                  </div>
                )}
              </div>
            )}

            {/* Progress Summary */}
            {bundleEntry && (
              <div className="bg-white rounded-lg border border-[#E5DFD0] p-4">
                <Label className="text-sm font-semibold text-[#1A1F2E]">Progress</Label>
                <div className="mt-3 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#5A5F6E]">Received:</span>
                    <span className="font-semibold text-green-600">{bundleEntry.received_bundles?.length || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#5A5F6E]">Pending:</span>
                    <span className={`font-semibold ${bundleEntry.pending_bundles?.length > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                      {bundleEntry.pending_bundles?.length || 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#5A5F6E]">Total Expected:</span>
                    <span className="font-semibold">{bundleEntry.expected_bundles?.length || 0}</span>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="mt-2">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full transition-all duration-300"
                        style={{ 
                          width: `${bundleEntry.expected_bundles?.length > 0 
                            ? (bundleEntry.received_bundles?.length / bundleEntry.expected_bundles.length * 100) 
                            : 0}%` 
                        }}
                      />
                    </div>
                  </div>
                </div>

                <p className="mt-4 text-xs text-[#5A5F6E] flex items-center gap-1">
                  <Check className="w-3.5 h-3.5 text-green-600" />
                  Each scan is saved instantly (ready for Sort) and closes on its own when all bundles are in.
                </p>
              </div>
            )}
          </div>

          {/* Right Panel - Scanned Bundles & Pending */}
          <div className="lg:col-span-2 space-y-4">
            {/* Scanned Bundles */}
            {bundleEntry && (
              <div className="bg-white rounded-lg border border-[#E5DFD0] p-4">
                <div className="flex justify-between items-center mb-4">
                  <Label className="text-sm font-semibold text-[#1A1F2E]">
                    Received Bundles ({bundleEntry.received_bundles?.length || 0})
                  </Label>
                </div>
                
                {bundleEntry.received_bundles?.length > 0 ? (
                  <div className="border border-[#E5DFD0] rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#F5F1E8] hover:bg-[#F5F1E8]">
                          <TableHead className="font-semibold text-[#1A1F2E] w-12">#</TableHead>
                          <TableHead className="font-semibold text-[#1A1F2E]">Bundle Barcode</TableHead>
                          <TableHead className="font-semibold text-[#1A1F2E]">Scanned At</TableHead>
                          <TableHead className="font-semibold text-[#1A1F2E]">Scanned By</TableHead>
                          {bundleEntry.status !== 'completed' && (
                            <TableHead className="font-semibold text-[#1A1F2E] w-16"></TableHead>
                          )}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bundleEntry.received_bundles.map((bundle, idx) => (
                          <TableRow key={bundle.bundle_barcode} className="hover:bg-[#FFFBF5]">
                            <TableCell className="font-medium">{idx + 1}</TableCell>
                            <TableCell className="font-mono text-sm">{bundle.bundle_barcode}</TableCell>
                            <TableCell className="text-sm text-[#5A5F6E]">{formatDateTime(bundle.scanned_at)}</TableCell>
                            <TableCell className="text-sm text-[#5A5F6E]">{bundle.scanned_by_name || '-'}</TableCell>
                            {bundleEntry.status !== 'completed' && (
                              <TableCell>
                                <Button
                                  onClick={() => setRemoveBundleTarget(bundle.bundle_barcode)}
                                  data-testid={`remove-bundle-${bundle.bundle_barcode}`}
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-[#5A5F6E]">
                    <Scan className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>No bundles scanned yet</p>
                    <p className="text-sm">Start scanning bundle barcodes</p>
                  </div>
                )}
              </div>
            )}

            {/* Pending Bundles */}
            {bundleEntry && bundleEntry.pending_bundles?.length > 0 && (
              <div className="bg-white rounded-lg border border-orange-200 p-4">
                <Label className="text-sm font-semibold text-orange-700">
                  Pending Bundles ({bundleEntry.pending_bundles.length})
                </Label>
                <div className="mt-3 flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                  {bundleEntry.pending_bundles.map((barcode) => (
                    <span 
                      key={barcode}
                      className="px-2 py-1 bg-orange-50 border border-orange-200 rounded text-xs font-mono text-orange-700"
                    >
                      {barcode}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* No Selection State */}
            {!selectedConsignment && (
              <div className="bg-white rounded-lg border border-[#E5DFD0] p-8 text-center">
                <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold text-[#1A1F2E] mb-2">Select a Document</h3>
                <p className="text-[#5A5F6E]">Choose a document from the dropdown to start bundle receipt</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <ConfirmRemoveBundleDialog
        barcode={removeBundleTarget}
        onConfirm={() => { handleRemoveBundle(removeBundleTarget); setRemoveBundleTarget(null); }}
        onCancel={() => setRemoveBundleTarget(null)}
      />
    </div>
  );
};

export default BundleReceiptEntry;
